import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { applyPageMetadata, applyStructuredData } from '../utils/seo';

export default function BartenderJobsNashvillePage() {
  useEffect(() => {
    const cleanupMeta = applyPageMetadata({
      title: 'Bartender Jobs in Nashville, TN | BarsConnect',
      description: 'Explore bartender jobs in Nashville, Tennessee. Find shifts in music venues, bars, and events with direct owner connections.',
      keywords: 'bartender jobs nashville tn, nashville bar jobs, nightlife jobs nashville, event bartender nashville, shift work nashville',
      canonicalUrl: 'https://barsconnect.com/bartender-jobs-nashville-tn',
      ogTitle: 'Bartender Jobs in Nashville, TN | BarsConnect',
      ogDescription: 'Find flexible bartender opportunities across Nashville nightlife and events.',
      twitterTitle: 'Bartender Jobs in Nashville, TN | BarsConnect',
      twitterDescription: 'Discover bartender shifts and connect with bars hiring now in Nashville.',
      robots: 'index,follow',
    });

    const cleanupSchema = applyStructuredData([
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://barsconnect.com/' },
          { '@type': 'ListItem', position: 2, name: 'Bartender Jobs', item: 'https://barsconnect.com/bartender-jobs' },
          { '@type': 'ListItem', position: 3, name: 'Nashville, TN', item: 'https://barsconnect.com/bartender-jobs-nashville-tn' },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'JobPosting',
        title: 'Bartender Jobs in Nashville, TN',
        description: 'Flexible bartender opportunities in Nashville, TN through BarsConnect.',
        employmentType: 'CONTRACTOR',
        hiringOrganization: {
          '@type': 'Organization',
          name: 'BarsConnect Partner Bars',
          sameAs: 'https://barsconnect.com',
        },
        jobLocation: {
          '@type': 'Place',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Nashville',
            addressRegion: 'TN',
            addressCountry: 'US',
          },
        },
      },
    ]);

    return () => {
      cleanupMeta();
      cleanupSchema();
    };
  }, []);

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8" data-testid="nashville-landing-page">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-white mb-3" data-testid="nashville-page-title">Bartender Jobs in Nashville, TN</h1>
        <p className="text-muted-foreground mb-8" data-testid="nashville-page-subtitle">
          Find bartender gigs in live-music venues, busy downtown bars, and private events across Nashville.
        </p>
        <Card className="bg-white/5 border border-white/10" data-testid="nashville-cta-card">
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground mb-4" data-testid="nashville-cta-copy">
              Nashville hospitality moves quickly—create your free bartender profile to get discovered for open shifts.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button asChild data-testid="nashville-create-profile-btn"><Link to="/register/bartenders">Create Free Bartender Profile</Link></Button>
              <Button asChild variant="outline" data-testid="nashville-browse-bartenders-btn"><Link to="/bartenders">Browse Bartenders</Link></Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
